import { OnImageSaveProps } from '@components/form-builder/form-builder';
import { Field } from '@components/form/dynamic-fields';
import { FieldType, FileFieldType, FormV2 } from '@components/form/v2';

import { request } from 'api/request';
import { isInTemplateRoute } from 'ui/router';

export type JoruneyActionFormImageResponse = {
  imageId: string;
};

export type JourneyActionForm = {
  shortId: string;
  name: string;
  description: string | null;
  status: string;
  fieldsConfig: FormV2;
  submissionMessageConfig: FormV2;
  journeyActionId: string;
  formUrl?: string | null;
  journey?: {
    shortId: string;
    title: string;
    workspace: string;
  };
};

export const loadJourneyActionForm = async (formId: string) => {
  const url = isInTemplateRoute()
    ? `v1/templates/journey-action-forms/${formId}/`
    : `v1/journey-action-forms/${formId}/`;
  const response = await request.get<JourneyActionForm>(url);
  return response.data;
};

export const loadJourneyActionForms = async (options?: {
  includePublished?: boolean;
  journeyId?: string;
  travellerJourneyId?: string;
}) => {
  const url = isInTemplateRoute()
    ? `v1/templates/journey-action-forms/`
    : `v1/journey-action-forms/`;
  const response = await request.get<JourneyActionForm[]>(url, {
    params: {
      include_published: options?.includePublished ? 'true' : undefined,
      ...(options?.journeyId ? { journey_id: options.journeyId } : {}),
      ...(options?.travellerJourneyId ? { traveller_journey_id: options.travellerJourneyId } : {}),
    },
  });
  return response.data;
};

export const updateJourneyActionForm = async (formId: string, data: Partial<JourneyActionForm>) => {
  const response = await request.patch<JourneyActionForm>(
    `v1/journey-action-forms/${formId}/`,
    data
  );
  return response.data;
};

export const createJourneyActionFormImage = async ({ data, setProgress }: OnImageSaveProps) => {
  const response = await request.post<JoruneyActionFormImageResponse>(
    `v1/journey-action-forms/image/`,
    data,
    {
      onUploadProgress: (progressEvent) => {
        if (setProgress) {
          const { loaded, total } = progressEvent;
          if (total) {
            setProgress(loaded / total);
          }
        }
      },
    }
  );
  return response.data;
};

export type FormAnswer =
  | {
      id: string;
      label: string;
      value: string;
      type: 'dateField' | 'formField' | 'dropdown' | 'radioGroup' | 'selectMulti';
    }
  | {
      id: string;
      label: string;
      value: FormFile;
      type: 'file';
    }
  | {
      id: string;
      label: string;
      value: FormFile[];
      type: 'file-multi';
    };

export type FormAnswers = Array<{
  h3: string;
  answers: FormAnswer[];
}>;

export type FormFile = {
  name: string;
  src: string;
  srcConverted?: string;
};

export type FormAnswerFieldType =
  | Exclude<FieldType, { kind: 'input-file' }>
  | (Omit<FileFieldType, 'value'> & { value?: Array<FormFile> });
export type FormAnswersV2 = Array<FormAnswerFieldType>;

export type Form = {
  name: string;
  isComplete: boolean;
  uuid: string;
  formAnswers: FormAnswers;
  fieldsConfig: Array<Field>;
};

export type FormResponseV2 = {
  name: string;
  description: string;
  isComplete: boolean;
  uuid: string;
  formAnswers: FormAnswersV2;
  fieldsConfig: FormV2;
};

export const loadForm = async (shortId: string) => {
  const response = await request.get<Form | FormResponseV2>(`v1/forms/${shortId}/form/`);
  return response.data;
};

export const getJourneyActionFormByJourneyActionId = async (
  journeyActionId: string,
  travellerJourneyId?: string
) => {
  const url = isInTemplateRoute()
    ? `v1/templates/journey-action-forms/journey-action`
    : `v1/journey-action-forms/journey-action`;
  const response = await request.get<JourneyActionForm>(`${url}/${journeyActionId}`, {
    params: travellerJourneyId ? { traveller_journey_id: travellerJourneyId } : undefined,
  });
  return response.data;
};
